export const STAFF_ROLES = [
  { value: 'admin',      label: 'Admin' },
  { value: 'sales',      label: 'Sales' },
  { value: 'operations', label: 'Operations' },
  { value: 'accounts',   label: 'Accounts' },
  { value: 'houseStaff', label: 'House Staff' },
];

export const STAFF_ROLE_LABELS = Object.fromEntries(STAFF_ROLES.map(({ value, label }) => [value, label]));

export const PERMISSION_KEYS = [
  'dashboard',
  'calendar',
  'players',
  'messages',
  'communications',
  'quotes',
  'invoices',
  'contracts',
  'contractTemplates',
  'emailTemplates',
  'visits',
  'settings',
  // PLYRMATCH only: Users.jsx hides the toggle for every other tenant.
  'mentorship',
];

export const PERMISSION_LABELS = {
  dashboard:         'Dashboard',
  calendar:          'Calendar',
  players:           'Players',
  messages:          'Messages',
  communications:    'Communications',
  quotes:            'Quotes',
  invoices:          'Invoices',
  contracts:         'Contracts',
  contractTemplates: 'Contract Templates',
  emailTemplates:    'Automation',
  visits:            'Visits',
  settings:          'Settings',
  mentorship:        'Mentorship',
};

// Role defaults — overridable per-user via the Team Members page.
export const DEFAULT_PERMISSIONS = {

  admin: {
    dashboard: true, calendar: true, players: true, messages: true, communications: true, quotes: true,
    invoices: true, contracts: true, contractTemplates: true,
    emailTemplates: true, visits: true, settings: true, mentorship: true,
  },

  sales: {
    dashboard: true, calendar: true, players: true, messages: true, communications: true, quotes: true,
    invoices: false, contracts: true, contractTemplates: true,
    emailTemplates: true, visits: true, settings: false, mentorship: true,
  },

  operations: {
    dashboard: true, calendar: true, players: true, messages: true, communications: true, quotes: false,
    invoices: false, contracts: false, contractTemplates: false,
    emailTemplates: false, visits: true, settings: false, mentorship: true,
  },

  accounts: {
    dashboard: true, calendar: false, players: true, messages: true, communications: true, quotes: true,
    invoices: true, contracts: true, contractTemplates: false,
    emailTemplates: false, visits: false, settings: false,
  },

  houseStaff: {
    dashboard: true, calendar: true, players: true, messages: true, communications: true, quotes: false,
    invoices: false, contracts: false, contractTemplates: false,
    emailTemplates: false, visits: true, settings: false,
  },

  // The single "coach" identity — role: 'coach' — gets full Academy Portal
  // access (see AuthContext's canManageAcademyPortal) plus this fixed slice
  // of Onboarding: Calendar and Players only, nothing else.
  coach: {
    dashboard: false, calendar: true, players: true, messages: false, communications: false, quotes: false,
    invoices: false, contracts: false, contractTemplates: false,
    emailTemplates: false, visits: false, settings: false,
  },

};

// Permissions saved before Mentorship existed have no entry for it. For an
// Admin that reads as on, so they aren't locked out and unable to change it
// themselves; every other role stays off until someone switches it on. Keep in
// step with canUseMentorship in functions/mentorship.js.
export function savedPermissions(userProfile) {
  const { permissions, staffRole } = userProfile || {};
  if (!permissions) return permissions;
  return staffRole === 'admin' && permissions.mentorship === undefined ? { ...permissions, mentorship: true } : permissions;
}

export function hasPermission(userProfile, permission) {
  if (!userProfile?.staffRole) return true;
  const perms = savedPermissions(userProfile) ?? DEFAULT_PERMISSIONS[userProfile.staffRole] ?? DEFAULT_PERMISSIONS.admin;
  return !!perms[permission];
}

// Separate axis from PERMISSION_KEYS above — PERMISSION_KEYS gates which
// portal *pages/nav items* a staffRole can open; AGENT_TOPIC_KEYS gates which
// subjects the "Ask the Assistant" AI agent (src/pages/admin/AskAssistant.jsx,
// functions/assistant.js) will answer questions about for that staffRole.
// Someone can see the Calendar page but still be blocked from asking the
// assistant about it, or vice versa — so these are intentionally not merged
// into PERMISSION_KEYS. Enforced server-side in functions/assistant.js (a
// duplicated copy of DEFAULT_AGENT_TOPICS lives there, since Cloud Functions
// can't import from src/ — same reason LEAD_STAGE_LABELS is duplicated in
// functions/index.js rather than shared).
export const AGENT_TOPIC_KEYS = [
  'agentFinances', 'agentPlayers', 'agentCalendar', 'agentLeads',
  'agentTasks', 'agentContracts', 'agentVisits', 'agentGroups', 'agentFeed', 'agentAttendance',
  'agentPartners', 'agentCalendarWrite', 'agentFinancesWrite', 'agentContractsWrite', 'agentTasksWrite',
];

export const AGENT_TOPIC_LABELS = {
  agentFinances: 'Finances (invoices & payments)',
  agentPlayers:  'Players & Programmes',
  agentCalendar: 'Calendar',
  agentLeads:    'Leads (CRM pipeline)',
  agentTasks:    'Tasks & follow-ups',
  agentContracts: 'Contract status',
  agentVisits:   'Visits & trials',
  agentGroups:   'Academy Portal groups',
  agentFeed:     'Academy Portal feed',
  agentAttendance: 'Academy Portal attendance & session notes',
  agentPartners: 'Partners & placements (agency tenants)',
  agentCalendarWrite: 'Create calendar events (write access)',
  agentFinancesWrite: 'Create invoice drafts (write access)',
  agentContractsWrite: 'Create contract drafts (write access)',
  agentTasksWrite: 'Create tasks (write access)',
};

// Role defaults — overridable per-user via the Team Members page, same
// "role default, per-user override" shape as DEFAULT_PERMISSIONS above.
export const DEFAULT_AGENT_TOPICS = {

  admin: {
    agentFinances: true, agentPlayers: true, agentCalendar: true, agentLeads: true,
    agentTasks: true, agentContracts: true, agentVisits: true, agentGroups: true, agentFeed: true, agentAttendance: true,
    agentPartners: true, agentCalendarWrite: true, agentFinancesWrite: true, agentContractsWrite: true, agentTasksWrite: true,
  },

  sales: {
    agentFinances: false, agentPlayers: true, agentCalendar: true, agentLeads: true,
    agentTasks: true, agentContracts: true, agentVisits: true, agentGroups: false, agentFeed: false, agentAttendance: false,
    agentPartners: true, agentCalendarWrite: false, agentFinancesWrite: false, agentContractsWrite: false, agentTasksWrite: false,
  },

  operations: {
    agentFinances: false, agentPlayers: true, agentCalendar: true, agentLeads: false,
    agentTasks: true, agentContracts: false, agentVisits: true, agentGroups: false, agentFeed: false, agentAttendance: false,
    agentPartners: false, agentCalendarWrite: false, agentFinancesWrite: false, agentContractsWrite: false, agentTasksWrite: false,
  },

  accounts: {
    agentFinances: true, agentPlayers: true, agentCalendar: false, agentLeads: false,
    agentTasks: false, agentContracts: true, agentVisits: false, agentGroups: false, agentFeed: false, agentAttendance: false,
    agentPartners: false, agentCalendarWrite: false, agentFinancesWrite: false, agentContractsWrite: false, agentTasksWrite: false,
  },

  houseStaff: {
    agentFinances: false, agentPlayers: true, agentCalendar: true, agentLeads: false,
    agentTasks: false, agentContracts: false, agentVisits: true, agentGroups: false, agentFeed: false, agentAttendance: false,
    agentPartners: false, agentCalendarWrite: false, agentFinancesWrite: false, agentContractsWrite: false, agentTasksWrite: false,
  },

  coach: {
    agentFinances: false, agentPlayers: true, agentCalendar: true, agentLeads: false,
    agentTasks: false, agentContracts: false, agentVisits: false, agentGroups: true, agentFeed: true, agentAttendance: true,
    agentPartners: false, agentCalendarWrite: false, agentFinancesWrite: false, agentContractsWrite: false, agentTasksWrite: false,
  },

};

export function hasAgentTopic(userProfile, topic) {
  if (!userProfile?.staffRole) return true;
  const topics = userProfile.agentTopics ?? DEFAULT_AGENT_TOPICS[userProfile.staffRole] ?? DEFAULT_AGENT_TOPICS.admin;
  return !!topics[topic];
}

// Which top-level portal sections a staff member can enter — separate from
// staffRole/PERMISSION_KEYS above, which only govern nav *within* onboarding.
// academyAdmin implicitly has both (see AuthContext); salesRep is granted
// one or both explicitly via the "access" custom claim.
export const ACCESS_SECTIONS = ['onboarding', 'crm'];

export const ACCESS_SECTION_LABELS = {
  onboarding: 'Onboarding',
  crm: 'CRM',
};

// Same additive model as ACCESS_SECTIONS above, but for guardian-role accounts
// choosing between the pre-arrival Player Portal and the at-academy Academy
// Portal — see AuthContext's guardian access default and the /portal/choose
// switcher.
export const GUARDIAN_ACCESS_SECTIONS = ['playerPortal', 'academyPortal'];

export const GUARDIAN_ACCESS_SECTION_LABELS = {
  playerPortal: 'Player Portal',
  academyPortal: 'Academy Portal',
};

export function hasAccess(access, section) {
  return Array.isArray(access) && access.includes(section);
}
